import React, { useState, useEffect } from 'react';
import { 
  Button, 
  TextField, 
  Box, 
  Snackbar, 
  Alert, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as XLSX from 'xlsx';
import axios from 'axios';
import PoligonColorPicker from '../../components/poligonos/poligonColorPicker';
import './marcaPoligonos.css';

const MarcaPoligonos = () => {
  const [data, setData] = useState([]);
  const [zona, setZona] = useState('');
  const [color, setColor] = useState('#FF0000');
  const [excelCargado, setExcelCargado] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [map, setMap] = useState(null);
  const [currentPolygon, setCurrentPolygon] = useState(null);
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawingPolygon, setDrawingPolygon] = useState(null);
  const [showSaveWarning, setShowSaveWarning] = useState(false);

  useEffect(() => {
    if (window.google) {
      initMap();
    } else {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD1Nl9yulU0jgl6VRGkK89neIalMrblrws&callback=initMap`;
      script.async = true;
      document.body.appendChild(script);
      window.initMap = initMap;
    }
  }, []);

  const initMap = () => {
    const mapInstance = new window.google.maps.Map(document.getElementById('map'), {
      center: { lat: -34.603722, lng: -58.381592 },
      zoom: 12,
    });
    setMap(mapInstance);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          if (!sheet) throw new Error('No se pudo leer la hoja del archivo');
          
          const json = XLSX.utils.sheet_to_json(sheet);
          if (json.length === 0) throw new Error('La hoja está vacía');
          
          setData(json);
          setExcelCargado(true);
          createPolygonFromData(json, color);
        } catch (error) {
          setData([]);
          setExcelCargado(false);
          showAlert('Error al cargar el archivo', 'error');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const createPolygonFromData = (polygonData, polygonColor) => {
    if (!map || polygonData.length === 0) return;
    
    if (currentPolygon) currentPolygon.setMap(null);
    if (drawingPolygon) {
      drawingPolygon.setMap(null);
      setDrawingPolygon(null);
    }

    const coordinates = polygonData.map(row => ({
      lat: parseFloat(row.lat),
      lng: parseFloat(row.lng),
    }));

    const newPolygon = new window.google.maps.Polygon({
      paths: coordinates,
      strokeColor: polygonColor,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: polygonColor,
      fillOpacity: 0.35,
      draggable: false,
      editable: true,
    });

    newPolygon.setMap(map);
    setCurrentPolygon(newPolygon);
    
    newPolygon.addListener('mouseup', () => {
      const paths = newPolygon.getPaths().getArray()[0].getArray();
      setData(paths.map(path => ({ lat: path.lat(), lng: path.lng() })));
    });
  };

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleSave = () => {
    if (drawingMode) {
      setShowSaveWarning(true);
      return;
    }

    if (!zona) {
      showAlert('Debe ingresar una zona', 'error');
      return;
    }

    let puntosAGuardar = [];
    if (currentPolygon) {
      const paths = currentPolygon.getPaths().getArray()[0].getArray();
      puntosAGuardar = paths.map(path => ({ lat: path.lat(), lng: path.lng() }));
    }

    if (puntosAGuardar.length === 0) {
      showAlert('No hay datos de polígono para guardar', 'error');
      return;
    }

    axios.post('https://mapa.ne3publicidad.com.ar/api/api/savePolygon', {
      nombre_zona: zona,
      puntos: puntosAGuardar,
      color: color,
    })
    .then(() => {
      showAlert('Datos guardados exitosamente', 'success');
      resetForm();
    })
    .catch(() => {
      showAlert('Error al guardar los datos', 'error');
    });
  };

  const resetForm = () => {
    setData([]);
    setExcelCargado(false);
    setZona('');
    document.getElementById('file-upload').value = '';
    if (currentPolygon) {
      currentPolygon.setMap(null);
      setCurrentPolygon(null);
    }
    if (drawingPolygon) {
      drawingPolygon.setMap(null);
      setDrawingPolygon(null);
    }
    setDrawingMode(false);
  };

  const toggleDrawingMode = () => {
    if (drawingMode) {
      setDrawingMode(false);
      if (drawingPolygon) {
        const paths = drawingPolygon.getPaths().getArray()[0].getArray();
        setData(paths.map(path => ({ lat: path.lat(), lng: path.lng() })));
        setCurrentPolygon(drawingPolygon);
        setDrawingPolygon(null);
      }
    } else {
      setDrawingMode(true);
      if (currentPolygon) currentPolygon.setMap(null);
      
      const newPolygon = new window.google.maps.Polygon({
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.35,
        editable: true,
      });
      
      newPolygon.setMap(map);
      setDrawingPolygon(newPolygon);
      
      const clickListener = map.addListener('click', (event) => {
        const path = newPolygon.getPaths().getArray()[0];
        path.push(event.latLng);
        setData(path.getArray().map(p => ({ lat: p.lat(), lng: p.lng() })));
      });
      
      newPolygon.clickListener = clickListener;
    }
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    if (currentPolygon) {
      currentPolygon.setOptions({
        strokeColor: newColor,
        fillColor: newColor
      });
    }
    if (drawingPolygon) {
      drawingPolygon.setOptions({
        strokeColor: newColor,
        fillColor: newColor
      });
    }
  };

  useEffect(() => {
    return () => {
      if (drawingPolygon?.clickListener) {
        window.google.maps.event.removeListener(drawingPolygon.clickListener);
      }
    };
  }, [drawingPolygon]);

  return (
    <div className="container2">
      <Box className="file-controls2">
        <input
          accept=".xlsx, .xls"
          style={{ display: 'none' }}
          id="file-upload"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="file-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
            id="excel-upload-button2"
          >
            Cargar Excel
          </Button>
        </label>
        {excelCargado && (
          <Box display="flex" alignItems="center">
            <CheckCircleIcon color="success" />
            <span>Archivo Excel cargado</span>
          </Box>
        )}
        <Box className="input-container">
          <TextField
            id="zona-input"
            placeholder="Zona"
            value={zona}
            onChange={(e) => setZona(e.target.value)}
            className="custom-input2"
          />
        </Box>
        <PoligonColorPicker
          selectedColor={color}
          onChange={handleColorChange}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          id="save-button2"
        >
          Guardar
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: drawingMode ? '#FF0000' : '#03A63C', color: 'white' }}
          onClick={toggleDrawingMode}
          startIcon={<img 
            src="https://img.icons8.com/?size=100&id=Si0V2yDb01x6&format=png&color=000000" 
            alt="Crear Forma" 
            width="20" 
            height="32" 
          />}
        >
          {drawingMode ? 'Finalizar Forma' : 'Crear Forma'}
        </Button>
      </Box>
      <Box mt={4}>
        <div id="map" className="map2"></div>
      </Box>

      {/* Diálogo de advertencia simplificado */}
      <Dialog
        open={showSaveWarning}
        onClose={() => setShowSaveWarning(false)}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title">Advertencia</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Debe finalizar el modo de dibujo primero antes de poder guardar.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowSaveWarning(false)} 
            color="primary"
            autoFocus
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MarcaPoligonos;